/** @jsxImportSource @emotion/react */
import { Header } from '@quickbit/backoffice-core-frontend';
import {
  addScaleToMoney,
  Card,
  Input,
  Table,
  Typography,
  useTheme,
  UtilityButton,
} from '@quickbit/qb-design-system';
import { useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAsyncFn, useMount } from 'react-use';
import { QBLoader } from 'components';
import { getSettlementReport } from 'tabs/AccountingSettlement/api';
import { formatCurrency } from 'utils';
import { downloadReport } from '../utils';
import { Adjustments, Payments } from './components';

const ORDER_TABLE_HEADERS = [
  { label: 'Completed At', cellKey: 'completedAt', sort: false },
  { label: 'Created At', cellKey: 'createdAt', sort: false },
  { label: 'Order ID', cellKey: 'orderId', sort: false },
  { label: 'Reference ID', cellKey: 'referenceId', sort: false },
  { label: 'Order Amount', cellKey: 'orderAmount', sort: false },
  { label: 'Settled Amount', cellKey: 'settlementAmount', sort: false },
  { label: 'Fee', cellKey: 'processingFee', sort: false },
];

const REFUND_TABLE_HEADERS = [
  { label: 'Refund Created At', cellKey: 'createdAt', sort: false },
  { label: 'Order ID', cellKey: 'orderId', sort: false },
  { label: 'Refund Amount', cellKey: 'refundAmount', sort: false },
];

export const Details = () => {
  const { reportId } = useParams();
  const navigate = useNavigate();
  const { spacing } = useTheme();

  if (!reportId) {
    void navigate('/accounting/reports');
    throw new Error('No report id');
  }

  const [{ value, loading: refreshLoading }, refresh] = useAsyncFn(() => {
    return getSettlementReport(reportId);
  }, [reportId]);

  useMount(() => {
    void refresh();
  });

  const orderRows = useMemo(
    () =>
      value?.orders.map((order) => ({
        value: {
          completedAt: order.completedAt?.toLocaleString(),
          createdAt: order.createdAt.toLocaleString(),
          orderId: order.uuid,
          referenceId: order.referenceId ?? undefined,
          settlementAmount: order.settlement?.euroAmount,
          processingFee: order.settlement?.euroFee,
          orderAmount: order.settlement
            ? (order.settlement.euroAmount + order.settlement.euroFee).toFixed(
                2
              )
            : undefined,
        },
      })),
    [value]
  );

  const refundRows = useMemo(
    () =>
      value?.refunds.map((refund) => ({
        value: {
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
          createdAt: refund.createdAt?.toLocaleString(),
          orderId: refund.orderId,
          refundAmount: formatCurrency(
            addScaleToMoney({
              amount: refund.refundAmount,
              currencyCode: refund.currency,
            })
          ),
        },
      })),
    [value]
  );

  return (
    <>
      <Header>
        {' '}
        <div
          css={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div
            css={{
              display: 'flex',
              gap: spacing.xxs,
              alignItems: 'center',
            }}
          >
            <Typography variant="subtitle">Report details</Typography>
            {value && (
              <Typography variant="bodyBold">
                {value.startTime.toLocaleString()} -{' '}
                {value.endTime.toLocaleString()}
              </Typography>
            )}
          </div>
          <div css={{ display: 'flex', gap: spacing.xs, alignItems: 'center' }}>
            {value && (
              <Typography variant="subtitle">
                {value.processed ? 'Settled ✅' : 'Settlement pending'}
              </Typography>
            )}
            {value && (
              <UtilityButton
                text="Download"
                onClick={() => {
                  downloadReport(value);
                }}
              />
            )}
          </div>
        </div>
      </Header>
      {refreshLoading && !value && <QBLoader />}
      {value && (
        <div
          css={{ display: 'flex', flexDirection: 'column', gap: spacing.xs }}
        >
          <div
            css={{
              display: 'flex',
              gap: spacing.xs,
            }}
          >
            <div css={{ flex: 1 }}>
              <Card>
                <Typography variant="subtitle">Amounts</Typography>
                <div
                  css={{
                    display: 'grid',
                    gap: spacing.xxs,
                    marginTop: spacing.xxs,
                  }}
                >
                  <Input
                    value={value.orderCount.toString()}
                    label="Number of created orders (this affects the gateway fee)"
                    endAdornment="#"
                  />
                  <Input
                    value={value.totalAmount.toString()}
                    label="Total Sales Volume"
                    endAdornment="€"
                  />
                  <Input
                    value={value.processingFee.toString()}
                    label="Processing Fee"
                    endAdornment="€"
                  />
                  <Input
                    value={value.gatewayFee.toString()}
                    label="Gateway Fee"
                    endAdornment="€"
                  />
                  <Input
                    value={value.settlementFee.toString()}
                    label="Settlement Fee"
                    endAdornment="€"
                  />
                  <Input
                    value={value.settlementAmount.toString()}
                    label="Net Settlement amount"
                    endAdornment="€"
                  />
                  <div
                    css={{ display: 'grid', gridTemplateColumns: '1fr 1fr' }}
                  >
                    <div />
                    <Input
                      css={{}}
                      value={value.amountRemainingToBePaid.toFixed(2)}
                      label="Amount remaining to be paid (including existing adjustments and payments)"
                      endAdornment="€"
                    />
                  </div>
                  <Adjustments adjustments={value.adjustments} />
                  <Payments payments={value.payments} />
                </div>
              </Card>
            </div>
          </div>
          <Card>
            <Typography variant="subtitle">Completed Orders</Typography>
            <Table headers={ORDER_TABLE_HEADERS} rows={orderRows ?? []} />
          </Card>
          <Card>
            <Typography variant="subtitle">Refunded Orders</Typography>
            <Table headers={REFUND_TABLE_HEADERS} rows={refundRows ?? []} />
          </Card>
        </div>
      )}
    </>
  );
};
